/* eslint-disable react/prop-types */
import React from 'react';

// Components
import LabeledTextAreaInput from '../components/shared/LabeledTextAreaInput';
import LabeledTextInput from '../components/shared/LabeledTextInput';
import LabeledNumericInput from '../components/shared/LabeledNumericInput';
import LabeledDatePicker from '../components/shared/LabeledDatePicker';
import LabeledSelect from '../components/shared/LabeledSelect';
import LabeledIfStatment from '../components/shared/LabeledIfStatment';
import LabledFetchSelect from '../components/shared/LabeledFetchSelect';
import LabledStatesInput from '../components/shared/LabledStatesInput';
import ReportRequiredLabel from '../components/shared/ReportRequiredLabel';

import messages from '../constants/messages';

// This is for fetch select
import { fetchUser, fetchCompanyMembers, getCompanyMembersForAdmin } from '../actions/profile/apiRequests';
import { fetchCompany } from '../actions/companies/apiRequests';

import { isInteger, validateRegExp, validateCommaSeparatedEmails } from './validators';
import { toDate } from './dates';

export const REQUIRED_FIELD_NAMES = ['ClientName', 'ClientEmail', 'PropertyAddress1'];

export const INVALID_REPORT_REQUIRED_FIELDS = ['InvalidReportReason', 'IstestValid'];

const componentsMap = {
  text: LabeledTextInput,
  select: LabeledSelect,
  intlabel: LabeledNumericInput,
  multitext: LabeledTextAreaInput,
  date: LabeledDatePicker,
  ifselect: LabeledIfStatment,
  typevalueselect: LabledFetchSelect,
  states: LabledStatesInput,
};

const validationMap = {
  Phone: messages.validation.phone,
};

const isRequiredField = (component, isTestNotValid) => {
  if (REQUIRED_FIELD_NAMES.includes(component.id)) {
    return true;
  }

  return isTestNotValid ? INVALID_REPORT_REQUIRED_FIELDS.includes(component.id) : component.required;
};

const isRequiredForInvalidReport = (component) => {
  return INVALID_REPORT_REQUIRED_FIELDS.includes(component.id) || REQUIRED_FIELD_NAMES.includes(component.id);
};

const BlankFieldValidationError = (component, isTestNotValid) => {
  if (isTestNotValid) {
    return isRequiredForInvalidReport(component) ? messages.validation.requiredForReport : '';
  }

  return isRequiredField(component, isTestNotValid)
    ? messages.validation.requiredForReport
    : messages.validation.blankField;
};

const checkIsValid = (validationType, expression, value) => {
  switch (validationType) {
    case 0:
    case 'RegExp': {
      return validateRegExp(expression, value);
    }
    case 1:
    case 'Email': {
      return validateCommaSeparatedEmails(value);
    }
    case 2:
    case 'Integer': {
      return isInteger(value);
    }
  }
  return true;
};

const isEditMode = (editMode, componentReadOnly, condition, isIsn, isLegacyDevice, componentId, value) => {
  if (condition === 'View') {
    return false;
  }

  if (condition === 'Edit' && componentReadOnly === 'Edit') {
    return false;
  }
  
  if (componentId === 'End') {
    return isLegacyDevice;
  }
  
  if (componentId === 'LastCalibration') {
    return isLegacyDevice || editMode;
  }

  if (componentReadOnly === 'Always') {
    return false;
  }

  if (componentReadOnly === 'Isn' && isIsn === 'true') {
    return false;
  }

  if (componentReadOnly === condition && (componentReadOnly === 'Edit' || componentReadOnly === 'Isn')) {
    return false;
  }

  if (componentReadOnly === 'AlphaDevice' && isLegacyDevice) {
    return false;
  }

  return editMode;
};

const isIsnField = (componentReadOnly, isIsn) => {
  if (componentReadOnly === 'Isn' && isIsn === 'true') {
    return true;
  }

  return false;
};

const fetchTester = (isfetchedById, values, isSpruceAdmin) => {
  if (isSpruceAdmin) {
    return getCompanyMembersForAdmin(1, 100, values['CompanyId']).then((users) => {
      return users.data.map((value, index) => {
        return {
          label: `${value.firstName} ${value.lastName}`,
          value: isfetchedById ? value.id : `${value.firstName} ${value.lastName}`,
        };
      });
    });
  } else {
    return fetchCompanyMembers().then((users) => {
      return users.data.map((value, index) => {
        return {
          label: `${value.firstName} ${value.lastName}`,
          value: isfetchedById ? value.id : `${value.firstName} ${value.lastName}`,
        };
      });
    });
  }
};

const fetchOptions = async (typeValue, values, isSpruceAdmin) => {
  switch (typeValue) {
    case 'Tester': {
      return fetchTester(true, values, isSpruceAdmin);
    }
    case 'TesterByName': {
      return fetchTester(false, values, isSpruceAdmin);
    }
  }
  return [];
};

const fetchValue = async (typeValue, value) => {
  if (!value) return null;
  switch (typeValue) {
    case 7:
    case 'Tester': {
      return fetchUser(value).then((user) => {
        return `${user.firstName} ${user.lastName}`;
      });
    }
    case 8:
    case 'Company': {
      return fetchCompany(value).then((company) => {
        return company.name;
      });
    }
  }
  return value;
};

const isVisible = (condition, visibility) => {
  if (condition === visibility && visibility === 'Create') {
    return true;
  }

  if ((condition === 'Edit' || condition === 'View') && visibility === 'ViewEdit') {
    return true;
  }

  if (visibility === null) {
    return true;
  }

  return false;
};

const getComponentLabel = (component, isTestNotValid) => {
  if (
    (isTestNotValid && INVALID_REPORT_REQUIRED_FIELDS.includes(component.id)) ||
    (!isTestNotValid && component.required)
  ) {
    return React.createElement(ReportRequiredLabel, {}, component.title);
  }

  return component.title;
};

const getComponentError = (component, errors = {}) => {
  if (!component || !component.id) {
    return '';
  }

  return errors[component.id] || '';
};

const shouldShowTimeInput = (componentType, componentId) => {
  if (componentType === 'date' && componentId === 'End') {
    return true;
  }

  return false
}

export const DynamicRenderControls = ({
  component,
  value,
  condition,
  isIsn,
  isLegacyDevice,
  onChange,
  onValidate,
  editMode,
  errors,
  isSpruceAdmin,
  ifValue = '',
  format,
  isTestNotValid,
}) => {
  if (!isVisible(condition, component.visibility)) {
    return;
  }

  const componentType = component.type.toLowerCase();

  if (!componentsMap[componentType]) {
    return;
  }

  if (componentType === 'ifselect') {
    return React.createElement(componentsMap[componentType], {
      editMode: isEditMode(editMode, component.readonly, condition, isIsn, isLegacyDevice),
      label: getComponentLabel(component, isTestNotValid),
      allowBlank: !isRequiredField(component, isTestNotValid),
      name: component.id,
      value: value[component.id],
      condition: value[component.id] === component.ifValue,
      onChange: (value) => {
        onChange(component.id, value.value === null ? undefined : value);

        // reset child field
        if (value !== component.ifValue) {
          onChange(component.child.id, undefined);
        }
      },
      onValidate: (error, isValidationError) => {
        onValidate(component.id, error, isValidationError);
      },
      emptyError: BlankFieldValidationError(component, isTestNotValid),
      error: getComponentError(component, errors, isTestNotValid),
      invalid: Boolean(errors[component.id]),
      options: component.values
        ? component.values.map((value, index) => {
            return {
              label: component.displayValues[index],
              value: value,
            };
          })
        : [],
      isnLabel: isIsnField(component.readonly, isIsn),
      child: React.createElement(componentsMap[component.child.type], {
        editMode: isEditMode(editMode, component.child.readonly, condition, isIsn, isLegacyDevice),
        label: getComponentLabel(component.child, isTestNotValid),
        allowBlank: !isRequiredField(component, isTestNotValid),
        name: component.child.id,
        value: ifValue,
        error: getComponentError(component.child, errors, isTestNotValid),
        invalid: Boolean(errors[component.child.id]),
        onChange: (value) => {
          if (component.child.type === 'multitext') {
            onChange(component.child.id, value);
          } else {
            onChange(component.child.id, value.value === null ? undefined : value);
          }
        },
        validator: (value) => {
          return checkIsValid(component.child.validationType, component.child.validation, value);
        },
        onValidate: (error, isValidationError) => {
          onValidate(component.child.id, error, isValidationError);
        },
        emptyError: BlankFieldValidationError(component.child, isTestNotValid),
        options: component.child.values
          ? component.child.values.map((value, index) => {
              return {
                label: component.child.displayValues[index],
                value: value,
              };
            })
          : [],
        visibility: isVisible(condition, component.child.visibility),
        isnLabel: isIsnField(component.child.readonly, isIsn),
        key: component.child.id,
      }),
      visibility: isVisible(condition, component.visibility),
      key: component.id,
    });
  }

  if (componentType === 'typevalueselect') {
    return React.createElement(componentsMap[componentType], {
      editMode: isEditMode(editMode, component.readonly, condition, isIsn, isLegacyDevice),
      label: getComponentLabel(component, isTestNotValid),
      allowBlank: !isRequiredField(component, isTestNotValid),
      name: component.id,
      fetchValue: async () => {
        return await fetchValue(component.typeValue, value[component.id]);
      },
      value: value[component.id],
      error: getComponentError(component, errors, isTestNotValid),
      invalid: Boolean(errors[component.id]),
      onChange: (value) => {
        onChange(component.id, value);
      },
      fetchOptions: async () => {
        return await fetchOptions(component.typeValue, value, isSpruceAdmin);
      },
      visibility: isVisible(condition, component.visibility),
      isnLabel: isIsnField(component.readonly, isIsn),
      emptyError: BlankFieldValidationError(component, isTestNotValid),
      key: component.id,
    });
  }

  if (componentType === 'states') {
    return React.createElement(componentsMap[componentType], {
      editMode: isEditMode(editMode, component.readonly, condition, isIsn, isLegacyDevice),
      label: getComponentLabel(component, isTestNotValid),
      allowBlank: !isRequiredField(component, isTestNotValid),
      name: component.id,
      value: value[component.id],
      error: getComponentError(component, errors, isTestNotValid),
      invalid: Boolean(errors[component.id]),
      onChange: (value) => {
        onChange(component.id, value);
      },
      validator: (value) => {
        return checkIsValid(component.validationType, component.validation, value);
      },
      onValidate: (error, isValidationError) => {
        onValidate(component.id, error, isValidationError);
      },
      emptyError: BlankFieldValidationError(component, isTestNotValid),
      CountryValue: value[component.countryWidgetId],
      visibility: isVisible(condition, component.visibility),
      isnLabel: isIsnField(component.readonly, isIsn),
      key: component.id,
    });
  }

  return React.createElement(componentsMap[componentType], {
    editMode: isEditMode(
      editMode,
      component.readonly,
      condition,
      isIsn,
      isLegacyDevice,
      component.id,
      value[component.id],
    ),
    label: getComponentLabel(component, isTestNotValid),
    allowBlank: !isRequiredField(component, isTestNotValid),
    name: component.id,
    value: value[component.id] && componentType === 'date' ? toDate(value[component.id]) : value[component.id],
    time: shouldShowTimeInput(componentType, component.id),
    maxLength: component.maxLength,
    error: getComponentError(component, errors, isTestNotValid),
    invalid: Boolean(errors[component.id]),
    onChange: (value) => {
      if (componentType === 'multitext') {
        onChange(component.id, value);
      } else {
        onChange(component.id, value === null || value.value === null ? undefined : value);
      }
    },
    validator: (value) => {
      return checkIsValid(component.validationType, component.validation, value);
    },
    onValidate: (error, isValidationError) => {
      onValidate(component.id, error, isValidationError);
    },
    validationError: validationMap[component.validationType],
    emptyError: BlankFieldValidationError(component, isTestNotValid),
    options: component.values
      ? component.values.map((value, index) => {
          return {
            label: component.displayValues[index],
            value: value,
          };
        })
      : [],
    isnLabel: isIsnField(component.readonly, isIsn),
    format,
    key: component.id,
  });
};



// WEBPACK FOOTER //
// src/utils/fieldManagement.js